// NOTE: After Karma removal in Phase 1 Plan 2 (CI-03), the previous
// jasmine.createSpy('...').and.returnValue(...) calls were replaced by plain
// arrow functions returning Promise.resolve(...). The behavioural contract is
// identical for any code that only awaits the mock's resolved values; tests
// that asserted spy call counts no longer have a runtime to run against
// because all twelve Karma specs were deleted in the same plan.
//
// If Karma is re-introduced in M2, restore the jasmine.createSpy form and add
// back the @types/jasmine devDependency. The MockKeycloakOptions shape is
// intentionally kept in sync with cypress/support/keycloak-mock.ts so the M2
// re-instrumentation can either keep this jasmine-based factory or migrate
// the whole project to Vitest with minimal refactor.

import { Provider } from '@angular/core';
import Keycloak from 'keycloak-js';

export interface MockKeycloakOptions {
  authenticated?: boolean;
  username?: string;
  roles?: string[];
  resourceRoles?: Record<string, string[]>;
  token?: string;
  /**
   * M2 Phase 11 Plan 5 (UI-M2-10) — Keycloak `tenant_id` user-attribute
   * claim. Mirrors the OPS-M2-02 mapper attached to the `cover-systray`
   * client. Leave undefined to simulate a session that predates the
   * mapper deploy (UserService falls back to '' in that case).
   */
  tenantId?: string;
}

export function createMockKeycloak(options: MockKeycloakOptions = {}): Partial<Keycloak> {
  const {
    authenticated = true,
    username = 'testuser',
    roles = ['user'],
    resourceRoles = {},
    token = 'mock-jwt-token',
    tenantId,
  } = options;

  const resourceAccess = Object.entries(resourceRoles).reduce((acc, [key, value]) => {
    acc[key] = { roles: value };
    return acc;
  }, {} as Record<string, { roles: string[] }>);

  const tokenParsed: Keycloak.KeycloakTokenParsed & Record<string, unknown> = {
    preferred_username: username,
    email: `${username}@test.com`,
    given_name: 'Test',
    family_name: 'User',
    name: 'Test User',
    realm_access: { roles },
    resource_access: resourceAccess,
  };
  if (tenantId !== undefined) {
    tokenParsed['tenant_id'] = tenantId;
  }

  return {
    authenticated,
    token,
    // keycloak-angular's `createAuthGuard` reads `keycloak.realmAccess` and
    // `keycloak.resourceAccess` directly off the instance (NOT off
    // `tokenParsed.realm_access`), so we must expose them at the root for any
    // route guarded by `canActivateWithRoles` to receive the mocked roles.
    realmAccess: { roles },
    resourceAccess,
    tokenParsed,
    init: (() => Promise.resolve(authenticated)) as Keycloak['init'],
    login: (() => Promise.resolve()) as Keycloak['login'],
    logout: (() => Promise.resolve()) as Keycloak['logout'],
    loadUserProfile: (() => Promise.resolve({
      username,
      email: `${username}@test.com`,
      firstName: 'Test',
      lastName: 'User'
    })) as Keycloak['loadUserProfile'],
    updateToken: (() => Promise.resolve(true)) as Keycloak['updateToken'],
    hasRealmRole: ((role: string) => roles.includes(role)) as Keycloak['hasRealmRole'],
    hasResourceRole: ((role: string, resource?: string) => {
      if (resource) {
        return resourceRoles[resource]?.includes(role) ?? false;
      }
      return Object.values(resourceRoles).some(r => r.includes(role));
    }) as Keycloak['hasResourceRole']
  };
}

export function provideMockKeycloak(options: MockKeycloakOptions = {}): Provider {
  return {
    provide: Keycloak,
    useValue: createMockKeycloak(options)
  };
}

export const mockKeycloakAuthenticated = provideMockKeycloak({ authenticated: true });
export const mockKeycloakUnauthenticated = provideMockKeycloak({ authenticated: false });
export const mockKeycloakAdmin = provideMockKeycloak({
  authenticated: true,
  username: 'admin',
  roles: ['admin', 'user'],
  tenantId: 'cover3d-direct',
});
// M2 Phase 11 Plan 5 (UI-M2-10) — tenant-aware presets replacing the legacy
// single-tenant `mockKeycloakTrybaUser`. The `tenant_id` claim mirrors the
// OPS-M2-02 Keycloak user-attribute mapper that ships the JWT claim consumed
// by UserService.tenantId. Add new tenants by appending to TENANT_SLUGS in
// src/app/shared/constants/tenants.ts and copying the pattern below.
export const mockKeycloakCover3DTenant = provideMockKeycloak({
  authenticated: true,
  username: 'c3d-admin',
  roles: ['user'],
  tenantId: 'cover3d-direct',
});
export const mockKeycloakTrybaTenant = provideMockKeycloak({
  authenticated: true,
  username: 'tryba-admin',
  roles: ['user'],
  tenantId: 'tryba',
});
